import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import "../styles/AddItemModal.new.css";
import { getData, postData, patchData, deleteData } from "../api/ApiServices";
import { supplierPart, SupplierResponse } from "../models/SupplierParts";
import AddSupplierPartForm from "../forms/AddSupplierPartForm";
import Pagination from "../view/common/pagination/Pagination";
import TableComponent from "./TableComponent";
import { ApiResponse } from "../models/uomtypes";
import { ContributorResponse } from "../models/contributortypes";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import CustomAlert from "../view/common/alert/CustomAlert";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { error } from "console";

const NewSupplierPart: React.FC = () => {
  const [supplierParts, setSupplierParts] = useState<supplierPart[]>([]);
  const [editingSupplierPart, setEditingSupplierPart] =
    useState<supplierPart | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);

  const [sortBy, setSortBy] = useState<string>("partDetails");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const  permission = useSelector((state:RootState) => state.permissions)
    
  const [initializeSupplier, isSupplierInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isSupplierInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeSupplier) {
      fetchSuppliers();
      fetchSupplierParts();
    }
  }, [
    initializeSupplier,
    currentPage,
    itemsPerPage,
    searchTerm,
    filters,
    sortBy,
    sortOrder,
  ]);

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<ContributorResponse>>(
        "/contributors",
        {
          page: 1,
          limit: 100,
          filter: {
            status: "ACTIVE",
          },
        }
      );
      if (response?.data?.data) {
        const supplierList = response.data.data.filter(
          (contributor: ContributorResponse & { type?: string }) =>
            contributor.type === "SUPPLIER" ||
            contributor.type === "CUSTOMER&SUPPLIER"
        );
        console.log(supplierList);

        setSuppliers(supplierList);
      } else {
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSupplierParts = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<{ data: SupplierResponse[] }>(
        "/supplierparts",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: filters,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      );

      console.log(response);

      if (response?.data?.data) {
        setSupplierParts(response.data.data);
        setTotalItems(response.data.pagination.total || 0);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setSupplierParts([]);
      }
    } catch (error) {
      console.error("Error fetching supplier parts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleSaveItem = async (part: supplierPart) => {
    setIsLoading(true);
    try {
      if (editingSupplierPart) {
        const areValuesEqual = (value1: any, value2: any): boolean => {
          if (value1 === value2) return true;
          if (value1 === null || value2 === null) return false;
          if (value1 === undefined || value2 === undefined) return false;

          if (value1 instanceof Date && value2 instanceof Date) {
            return value1.getTime() === value2.getTime();
          }

          if (typeof value1 === "object" && typeof value2 === "object") {
            const keys1 = Object.keys(value1);
            const keys2 = Object.keys(value2);

            if (keys1.length !== keys2.length) return false;

            return keys1.every((key) =>
              areValuesEqual(value1[key], value2[key])
            );
          }

          if (typeof value1 === "number" && typeof value2 === "number") {
            return Math.abs(value1 - value2) < 0.00001;
          }

          if (
            (value1 === "" && (value2 === null || value2 === undefined)) ||
            (value2 === "" && (value1 === null || value1 === undefined))
          ) {
            return true;
          }

          if (typeof value1 !== "object" && typeof value2 !== "object") {
            return String(value1) === String(value2);
          }

          return value1 === value2;
        };

        let hasChanges = false;

        const fieldsToCompare = [
          "supplierId",
          "partId",
          "currency",
          "unitPrice",
          "gst",
          "sgst",
          "cgst",
          "igst",
          "status",
        ];

        for (const field of fieldsToCompare) {
          const originalValue = (editingSupplierPart as any)[field];
          const newValue = (part as any)[field];

          if (!areValuesEqual(originalValue, newValue)) {
            console.log(
              `Field ${field} changed from ${originalValue} to ${newValue}`
            );
            hasChanges = true;
            break;
          }
        }

        if (!hasChanges) {
          configureAlert(
            `No changes were made .`,
            () => {
              closeAlert();
              setIsAdding(false);
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
          setIsLoading(false);
          return;
        }

        await patchData("/supplierparts", editingSupplierPart._id || "", part);
        setEditingSupplierPart(null);

        configureAlert(
          `Supplier part has been updated successfully.`,
          () => {
            fetchSupplierParts();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
        setIsAdding(false);
      } else {
        const response: any = await postData("/supplierparts", part);
        console.log(response);

        if (response.statusCode === 1134) {
          configureAlert(
            `New supplier part has been added successfully.`,
            () => {
              fetchSupplierParts();
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        } else {
          throw new Error("unexpected response!");
        }
        setIsAdding(false);
      }
    } catch (error) {
      console.error("Error saving supplier part:", error);

      configureAlert(
        `Duplicate entry!`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        1000
      );
      setIsAdding(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteItem = async (id: string) => {
    const partToDelete = supplierParts.find((part) => part._id === id);

    if (partToDelete) {
      configureAlert(
        `Are you sure you want to InActive this supplier part?`,
        async () => {
          closeAlert(); 
          setIsLoading(true);

          try {
            await deleteData("/supplierparts", id);
            configureAlert(
              `Supplier part has been InActive successfully.`,
              () => {
                fetchSupplierParts();
                closeAlert(); 
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          } catch (error) {
            console.error("Error deleting supplier part:", error);
            configureAlert(
              "Failed to delete supplier part.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          } finally {
            setIsLoading(false);
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleEdit = (part: supplierPart) => {
    setEditingSupplierPart(part);
    setIsAdding(true);
  };

  const handleClosePopup = () => {
    if (isAdding) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsAdding(false);
          setEditingSupplierPart(null);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsAdding(false);
      setEditingSupplierPart(null);
    }
  };

  const columns = [
    {
      key: "supplierparts",
      label: "Supplier Code",
      nameKey: "code", 
      render: (value: any) => (typeof value === "object" ? value?.code : "N/A"),
    },
    {
      key: "partDetails",
      label: "PartNo",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
    },
    {
      key: "partDetails",
      label: "PartName",
      nameKey: "partName", 
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
    },
    {
      key: "unitPrice",
      label: "Unit Price",
      isRightAlignment: true,
      isRightAlign: true,
    },
    { key: "currency", label: "Currency" },
    { key: "gst", label: "GST (%)",isRightAlign: true },
    { key: "sgst", label: "SGST (%)",isRightAlign: true },
    { key: "cgst", label: "CGST (%)",isRightAlign: true },
    { key: "igst", label: "IGST (%)",isRightAlign: true },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Supplier Parts</span>
            </div>

            {permission.menus.supplierparts.create ?
            <div
              className="items-button-container tooltip-wrapper"
              onClick={() => setIsAdding(true)}
            >
              <button className="item-models-add-btn">
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>
      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={supplierParts}
            onEdit={handleEdit}
            onDelete={handleDeleteItem}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            context="supplierParts"
            excludeFields={["internalPartNo", "externalPartNo", "hsnCode"]}
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
      {isAdding && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h2>
                {editingSupplierPart
                  ? "Edit Supplier Part"
                  : "Add New Supplier Part"}
              </h2>
              <button className="close-button" onClick={handleClosePopup}>
                <X size={20} />
              </button>
            </div>
            <div className="popup-content">
              <AddSupplierPartForm
                onSave={handleSaveItem}
                onCancel={handleClosePopup}
                suppliers={suppliers}
                isEditing={!!editingSupplierPart}
                editingSupplierPart={editingSupplierPart}
                isProcessing={isLoading}
              />
            </div>
          </div>
        </div>
      )}
      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default NewSupplierPart;
